import React, { useMemo, useState } from 'react';
import styles from 'LearningManager/styles/CreateAssignment.module.scss';
import {
	SetupOptions,
	AssignmentSetupType,
	quickSetupSteps,
	customSetupSteps,
	getScheduledEditSteps
} from 'LearningManager/constants/createAssignment.constant';
import SetupCard from 'LearningManager/components/SetupCard';
import { Icon } from 'Components';
import { useHistory, useLocation } from 'react-router-dom';
import Stepper from 'LearningManager/components/Stepper';
import {
	StepperProvider,
	useStepper
} from 'LearningManager/hooks/Stepper.context';
import CreateExitModal from 'LearningManager/components/CreateExitModal';
import Button from 'LearningManager/components/Button';
import AssignmentSuccessModal from 'LearningManager/components/AssignmentSuccessModal';
import classNames from 'classnames';

export const useQuery = () => {
	const location = useLocation();
	return useMemo(() => new URLSearchParams(location.search), [
		location.search
	]);
};

const CreateAssignment: React.FC = () => {
	const query = useQuery();

	const currentSetupType = query.get(
		'setupType'
	) as AssignmentSetupType | null;
	const isScheduled = query.get('isScheduled') === 'true';

	const steps = (() => {
		if (!currentSetupType) return null;
		if (isScheduled) return getScheduledEditSteps(currentSetupType);
		if (currentSetupType === 'Quick Setup') return quickSetupSteps;
		if (currentSetupType === 'Custom Setup') return customSetupSteps;
		return null;
	})();

	return (
		<StepperProvider>
			<div id="lms-ca-screen" className={styles.fullScreen}>
				<Header />
				<div id="lms-ca-main" className={styles.root}>
					<Stepper />
					<div id="lms-ca-step-body" className={styles.mainContainer}>
						<StepRenderer
							currentSetupType={currentSetupType}
							steps={steps}
						/>
					</div>
					<NavigationButtons steps={steps} />
				</div>
			</div>
		</StepperProvider>
	);
};

interface StepRendererProps {
	currentSetupType: AssignmentSetupType | null;
	steps: typeof quickSetupSteps | typeof customSetupSteps | null;
}

const StepRenderer: React.FC<StepRendererProps> = ({
	currentSetupType,
	steps
}) => {
	const { current } = useStepper();
	const CurrentStep = steps?.[current]?.component;

	if (!currentSetupType || !CurrentStep) {
		return <SelectCourse />;
	}

	return <CurrentStep />;
};

const Header: React.FC = () => {
	const history = useHistory();
	const query = useQuery();
	const [showModal, setShowModal] = useState(false);
	const isEditMode = Boolean(query.get('assignmentId'));

	return (
		<div id="lms-ca-header" className={styles.headerWrapper}>
			<div className={styles.header}>
				<div
					id="lms-ca-header-title"
					className={styles.header__heading}
				>
					{isEditMode ? 'Edit Assignments' : 'Create Assignment'}
				</div>
				<div id="lms-ca-header-desc" className={styles.header__desc}>
					{isEditMode
						? 'Edit training and course assignments'
						: 'Quickly assign training to your team'}
				</div>
			</div>

			<Icon
				id="lms-ca-header-close"
				type="close"
				role="img"
				onClick={() => setShowModal(true)}
			/>

			<CreateExitModal
				visible={showModal}
				onCancel={() => setShowModal(false)}
				onExit={() => history.push('/learning-manager')}
			/>
		</div>
	);
};

const SelectCourse: React.FC = () => {
	const { setupType, setSetupType } = useStepper();

	return (
		<div id="lms-ca-select-course" className={styles.selectCourse}>
			<div className={styles.selectCourse__wrapper}>
				<div
					id="lms-ca-select-course-heading"
					className={styles.selectCourse__heading}
				>
					Select Courses
				</div>
				<div
					id="lms-ca-select-course-desc"
					className={styles.selectCourse__desc}
				>
					Choose from among Venza's industry-leading course offerings
				</div>
			</div>

			<div id="lms-ca-setup-container" className={styles.setupContainer}>
				{SetupOptions.map((opt) => (
					<SetupCard
						key={opt.title}
						id={`lms-ca-setup-card-${opt.title
							.toLowerCase()
							.replace(/\s+/g, '-')}`}
						{...opt}
						isActive={setupType === opt.title}
						onClick={() =>
							setSetupType(opt.title as AssignmentSetupType)
						}
					/>
				))}
			</div>
		</div>
	);
};

interface NavigationProps {
	steps: typeof quickSetupSteps | typeof customSetupSteps | null;
}

const NavigationButtons: React.FC<NavigationProps> = ({ steps }) => {
	const history = useHistory();
	const location = useLocation();
	const {
		current,
		setupType,
		steps: stepItems,
		draft,
		next,
		back,
		isScheduledEdit
	} = useStepper();
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const query = useQuery();

	const currentSetupType = query.get('setupType');

	const onBack = () => {
		if (current > 0) {
			back();
		}
	};

	const onNext = () => {
		if (!currentSetupType) {
			if (!setupType) return;

			const params = new URLSearchParams(location.search);
			params.set('setupType', setupType);

			history.push({
				pathname: location.pathname,
				search: params.toString()
			});
			return;
		}

		if (steps && current < steps.length - 1) {
			next();
			return;
		}

		if (currentSetupType && current === stepItems.length - 1) {
			setShowSuccessModal(true);
		}
	};

	const redirectToCourses = () => {
		history.push('/learning-manager');
	};

	const isLastStep = currentSetupType && current === stepItems.length - 1;
	const nextLabel = isLastStep ? 'Confirm & Assign' : 'Next';
	const hideBackButton = currentSetupType === 'Quick Setup' && current === 0;

	const hasConfigureData = Boolean(
		draft.configure.assignmentName.trim() &&
			draft.configure.certDuration &&
			draft.configure.assignmentStart &&
			draft.configure.dueDate &&
			draft.configure.assignmentType
	);

	const hasCustomSelection =
		draft.customSetup.selectedLearningPathCourseIds.length > 0 ||
		draft.customSetup.selectedIndividualCourseIds.length > 0;

	const hasAssignUsersSelection = (() => {
		switch (draft.customSetup.selectedAssignUsersOptionId) {
			case 214:
				return draft.customSetup.selectedOrganizationIds.length > 0;
			case 35:
				return draft.customSetup.selectedPropertyIds.length > 0;
			case 634:
				return draft.customSetup.selectedRoleIds.length > 0;
			case 676:
				return draft.customSetup.selectedGroupIds.length > 0;
			case 784:
				return draft.customSetup.selectedIndividualUserIds.length > 0;
			default:
				return false;
		}
	})();

	const isNextDisabled = (() => {
		if (!currentSetupType) return false;

		// Scheduled edit: only Configure (step 0) needs validation
		if (isScheduledEdit) {
			if (current === 0) return !hasConfigureData;
			return false;
		}

		if (setupType === 'Quick Setup') {
			if (current === 1) return !hasConfigureData;
			return false;
		}

		if (setupType === 'Custom Setup') {
			if (current === 0) return !hasCustomSelection;
			if (current === 1) return !hasAssignUsersSelection;
			if (current === 2) return !hasConfigureData;
			return false;
		}

		return false;
	})();

	return (
		<div
			id="lms-ca-nav"
			className={classNames(styles.navigationButtons, {
				[styles['navigationButtons--flex-end']]: hideBackButton
			})}
		>
			{!hideBackButton && (
				<Button
					id="lms-ca-nav-back"
					variant="border"
					size="small"
					onClick={onBack}
					disabled={current === 0}
				>
					<Icon
						type="chevronUp"
						role="img"
						style={{ rotate: '-90deg' }}
					/>
					Back
				</Button>
			)}

			<Button
				id="lms-ca-nav-next"
				size="small"
				onClick={onNext}
				className={isLastStep ? 'cta-prominent' : undefined}
				disabled={isNextDisabled}
			>
				{nextLabel}{' '}
				{!isLastStep && (
					<Icon
						type="chevronUp"
						role="img"
						style={{ rotate: '90deg' }}
					/>
				)}
			</Button>
			<AssignmentSuccessModal
				visible={showSuccessModal}
				onClose={redirectToCourses}
			/>
		</div>
	);
};

export default CreateAssignment;
